import { inject, Injectable } from '@angular/core';
import { RxStomp, RxStompState } from '@stomp/rx-stomp';
import { environment } from '../../environments/environment';
import { filter, firstValueFrom, from, map, mergeMap, shareReplay } from 'rxjs';
import { JsonUtils } from '../utils/json.utils';
import { BrowserSessionService } from './browser-session.service';
import { AuthenticationService } from './authentication.service';

@Injectable({ providedIn: 'root' })
export class RealTimeService {
  
  private _rxStomp: RxStomp;
  private _isRealTimeInit$: Promise<void>;
  
  private readonly browserSessionService: BrowserSessionService = inject(BrowserSessionService);
  private readonly authenticationService: AuthenticationService = inject(AuthenticationService);
  
  private connectionState$ = from(this.getStompConnection()).pipe(
    mergeMap(rxStomp => rxStomp.connectionState$),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  
  constructor() {
    this.initRealtime(this.browserSessionService.sessionId);
  }
  
  public async sendDataOnStomp(data: unknown, destination: string) {
    await this.getStompConnection();
    this._rxStomp.publish({ destination: `/${destination}`, body: JSON.stringify(data) });
  }
  
  public watch<T>(destination: string, prefix: string = '') {
    return from(this.getStompConnection())
      .pipe(
        mergeMap(() => this._rxStomp.watch(`${prefix}/topic/${destination}`)),
        map(message => JsonUtils.parseJsonIfValid(message.body, message.body) as T)
      );
    
  }
  
  public isConnected$() {
    return this.connectionState$.pipe(
      filter(state => state === RxStompState.OPEN)
    );
  }
  
  public terminateConnection(): void {
    this._rxStomp.deactivate({ force: true }).then(() => {
      this._rxStomp = undefined;
    });
  }
  
  private async getStompConnection() {
    await this._isRealTimeInit$;
    return this._rxStomp;
  }
  
  private initRealtime(sessionId: string): void {
    this._isRealTimeInit$ = new Promise((resolve) => {
      this.initRxStompConnexion(sessionId);
      resolve();
    });
  }
  
  private initRxStompConnexion(sessionId: string): void {
    if (this._rxStomp?.active) {
      return;
    }
    
    this._rxStomp = this.createRxStompConnection(sessionId);
    this._rxStomp.activate();
  }
  
  private createRxStompConnection(sessionId: string) {
    const rxStomp = new RxStomp();
    
    rxStomp.configure({
      beforeConnect: async (client) => {
        if (this.authenticationService.tokenNeedsRefreshing()) {
          await firstValueFrom(this.authenticationService.refreshAuthentication$());
        }
        
        client.configure({
          connectHeaders: {
            'browser-session-id': sessionId,
            'Authorization': `Bearer ${this.authenticationService.getAccessToken()}`
          },
        });
      },
      brokerURL: `${environment.broker_api_base_url}real-time`,
      reconnectDelay: 2000,
      debug: (msg: string): void => (environment.log.websocket ? console.log(new Date(), msg) : null)
    });
    
    return rxStomp;
  }
  
}
